import React, {
    useState,
    useEffect,
    useRef,
    useCallback,
    forwardRef,
    useImperativeHandle,
    useMemo,
    memo,
    useReducer
} from 'react';
import {
    Grid,
    TextField,
    MenuItem,
    Divider,
    Button,
    Typography,
    Box,
    Chip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow
} from '@material-ui/core';
import VisibilityIcon from '@material-ui/icons/Visibility';
import UndoIcon from '@material-ui/icons/Undo';
import CheckboxSelection from '../CheckboxSelection';
import GeneralInfoValidation from './GeneralInfoValidation';
import ProviderService from "../../../../Services/ProviderService";

// Extremely optimized input field that only re-renders when its specific value changes
const OptimizedField = memo(({
    field,
    value,
    onChange,
    label,
    required = false,
    error = null,
    type = "text",
    disabled = false,
    select = false,
    children = null,
    InputLabelProps = null
}) => {
    // Use a local state to avoid re-renders from parent
    const [localValue, setLocalValue] = useState(value || '');

    // Update local value when prop value changes significantly
    useEffect(() => {
        if (value !== localValue) {
            setLocalValue(value || '');
        }
    }, [value]);

    // Handle changes locally and propagate to parent after delay
    const handleChange = (e) => {
        const newValue = e.target.value;
        setLocalValue(newValue);

        // Use requestAnimationFrame for smoother UI
        requestAnimationFrame(() => {
            onChange(field, newValue);
        });
    };

    return (
        <TextField
            fullWidth
            label={label}
            value={localValue}
            onChange={handleChange}
            variant="outlined"
            required={required}
            error={!!error}
            helperText={error}
            type={type}
            disabled={disabled}
            select={select}
            InputLabelProps={InputLabelProps || (type === 'date' ? { shrink: true } : undefined)}
        >
            {children}
        </TextField>
    );
}, (prevProps, nextProps) => {
    // Only re-render if these specific props change
    return (
        prevProps.value === nextProps.value &&
        prevProps.error === nextProps.error &&
        prevProps.disabled === nextProps.disabled
    );
});

// Form state reducer to batch updates and improve performance
function formReducer(state, action) {
    switch (action.type) {
        case 'FIELD_CHANGE':
            return { ...state, [action.field]: action.value };
        case 'BATCH_UPDATE':
            return { ...state, ...action.updates };
        case 'RESET':
            return { ...action.data };
        default:
            return state;
    }
}

// Error state reducer
function errorReducer(state, action) {
    switch (action.type) {
        case 'SET_ERRORS':
            return { ...action.errors };
        case 'CLEAR_ERROR':
            const newState = { ...state };
            delete newState[action.field];
            return newState;
        case 'RESET':
            return {};
        default:
            return state;
    }
}

// Extracted dialog component
const ChangesSummaryDialog = memo(({ open, onClose, changesSummary }) => (
    <Dialog
        open={open}
        onClose={onClose}
        maxWidth="md"
        fullWidth
    >
        <DialogTitle>
            <Box display="flex" justifyContent="space-between" alignItems="center">
                <Typography variant="h6">Review Changes</Typography>
            </Box>
        </DialogTitle>
        <DialogContent>
            {changesSummary.length > 0 ? (
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell style={{ fontWeight: 'bold' }}>Field</TableCell>
                            <TableCell style={{ fontWeight: 'bold' }}>Original Value</TableCell>
                            <TableCell style={{ fontWeight: 'bold' }}>New Value</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {changesSummary.map((change, index) => (
                            <TableRow key={index}>
                                <TableCell>{change.field}</TableCell>
                                <TableCell>
                                    {change.field === 'Degrees' || change.field === 'Languages' ? (
                                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                                            {change.originalValue ?
                                                change.originalValue.split(', ').map((item, i) => (
                                                    <Chip
                                                        key={i}
                                                        label={item}
                                                        size="small"
                                                        style={{
                                                            backgroundColor: '#e3f2fd',
                                                            fontSize: '0.75rem',
                                                            margin: '2px'
                                                        }}
                                                    />
                                                ))
                                                : 'None'
                                            }
                                        </div>
                                    ) : (
                                        change.originalValue || 'Not Set'
                                    )}
                                </TableCell>
                                <TableCell>
                                    {change.field === 'Degrees' || change.field === 'Languages' ? (
                                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                                            {change.newValue ?
                                                change.newValue.split(', ').map((item, i) => (
                                                    <Chip
                                                        key={i}
                                                        label={item}
                                                        size="small"
                                                        style={{
                                                            backgroundColor: '#e8f5e9',
                                                            fontSize: '0.75rem',
                                                            margin: '2px'
                                                        }}
                                                    />
                                                ))
                                                : 'None'
                                            }
                                        </div>
                                    ) : (
                                        change.newValue || 'Not Set'
                                    )}
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            ) : (
                <Typography>No changes to review.</Typography>
            )}
        </DialogContent>
        <DialogActions>
            <Button onClick={onClose} color="primary">
                Close
            </Button>
        </DialogActions>
    </Dialog>
));

// Optimized checkbox selection wrapper
const OptimizedCheckboxSelection = memo(({ title, value, onChange, options, field, error, height }) => {
    const handleSelectionChange = useCallback((field, newValue) => {
        onChange(field, newValue);
    }, [onChange, field]);

    return (
        <CheckboxSelection
            title={title}
            value={value || []}
            onChange={handleSelectionChange}
            options={options}
            field={field}
            error={error}
            sortAlphabetically={false}
            height={height}
        />
    );
}, (prevProps, nextProps) => {
    // Custom comparison for arrays
    const valueEqual =
        prevProps.value?.length === nextProps.value?.length &&
        prevProps.value?.every((v, i) => v === nextProps.value[i]);

    const optionsEqual =
        prevProps.options?.length === nextProps.options?.length &&
        prevProps.options?.every((v, i) => v === nextProps.options[i]);

    return valueEqual &&
        optionsEqual &&
        prevProps.error === nextProps.error;
});

const PhysicianGeneralEdit = forwardRef(({
    physician,
    selectedTin,
    onValidationFunction,
    onSectionSaveLocal,
    onSectionDiscardChanges,
    onSectionHasChanges
}, ref) => {
    // Use reducers instead of multiple useState calls
    const [formState, dispatchForm] = useReducer(formReducer, {
        firstName: '',
        middleName: '',
        lastName: '',
        degrees: [],
        dateOfBirth: '',
        gender: '',
        languages: [],
        npi: '',
        lob: '',
    });

    const [errors, dispatchErrors] = useReducer(errorReducer, {});

    // Other state
    const [originalData, setOriginalData] = useState(null);
    const [hasChanges, setHasChanges] = useState(false);
    const [availableDegrees, setAvailableDegrees] = useState([]);
    const [availableLanguages, setAvailableLanguages] = useState([]);
    const [localSaveMessage, setLocalSaveMessage] = useState("");
    const [isValidated, setIsValidated] = useState(false);
    const [showChangesSummary, setShowChangesSummary] = useState(false);
    const [changesSummary, setChangesSummary] = useState([]);

    // Refs
    const formRef = useRef(null);
    const changeDetectionTimerRef = useRef(null);
    const pendingUpdatesRef = useRef({});
    const batchUpdateTimerRef = useRef(null);

    // Constants
    const LOB_OPTIONS = useMemo(() => [
        { value: 'AUTO', label: 'Auto' },
        { value: 'WC', label: 'Workers Comp' },
        { value: 'BOTH', label: 'Both' }
    ], []);

    // Format date function - memoized
    const formatDateForInput = useCallback((dateString) => {
        if (!dateString) return '';

        // Try to parse the date string
        const date = new Date(dateString);

        // Check if the date is valid
        if (isNaN(date.getTime())) {
            return '';
        }

        // Format as YYYY-MM-DD for the date input
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');

        return `${year}-${month}-${day}`;
    }, []);

    // Determine LOB value function - memoized
    const determineLobValue = useCallback((lobString) => {
        if (!lobString) return '';

        const lobLower = lobString.toLowerCase();

        // Check for both Auto and Workers Comp
        if (lobLower.includes('auto') && (lobLower.includes('workers comp') || lobLower.includes('wc'))) {
            return 'BOTH';
        }
        // Check for just Auto
        else if (lobLower.includes('auto')) {
            return 'AUTO';
        }
        // Check for just Workers Comp
        else if (lobLower.includes('workers comp') || lobLower.includes('wc')) {
            return 'WC';
        }

        return '';
    }, []);

    // Initialize form data when physician changes
    useEffect(() => {
        const initialData = {
            firstName: physician.phyFirstName || '',
            middleName: physician.phyMiddleName || '',
            lastName: physician.phyLastName || '',
            degrees: physician.degreeStr ? physician.degreeStr.split(',').map(d => d.trim()).filter(Boolean) : [],
            dateOfBirth: formatDateForInput(physician.phyDateOfBirth) || '',
            gender: physician.phyGender || '',
            languages: physician.languageStr ? physician.languageStr.split(',').map(l => l.trim()).filter(Boolean) : [],
            npi: physician.prvNpi || '',
            //lob: determineLobValue(physician.lobString),
            lob: physician.lobString
        };

        // Set original data
        setOriginalData(initialData);

        // Reset form state using reducer
        dispatchForm({ type: 'RESET', data: initialData });

        // Reset errors
        dispatchErrors({ type: 'RESET' });

        // Reset other state
        setIsValidated(false);
        setHasChanges(false);

        // Clear any pending timers
        if (changeDetectionTimerRef.current) {
            clearTimeout(changeDetectionTimerRef.current);
        }
        if (batchUpdateTimerRef.current) {
            clearTimeout(batchUpdateTimerRef.current);
        }

        // Clear pending updates
        pendingUpdatesRef.current = {};

    }, [physician, formatDateForInput, determineLobValue]);

    // Fetch reference data (degrees and languages)
    useEffect(() => {
        const fetchReferenceData = async () => {
            try {
                // Use Promise.all to fetch both in parallel
                const [degreesResponse, languagesResponse] = await Promise.all([
                    ProviderService.getDegrees(physician.prvId),
                    ProviderService.getLanguages(physician.prvId)
                ]);

                if (degreesResponse?.data) {
                    const degrees = degreesResponse.data.map(record => record.ndrDegree);
                    setAvailableDegrees(degrees);
                }

                if (languagesResponse?.data) {
                    const languages = languagesResponse.data.map(record => record.lngLanguage);
                    setAvailableLanguages(languages);
                }
            } catch (error) {
                console.error('Error fetching reference data:', error);
            }
        };

        if (physician.prvId) {
            fetchReferenceData();
        }
    }, [physician.prvId]);

    // Efficient comparison function
    const hasDataChanged = useCallback((current, original) => {
        if (!original) return false;

        // Fast path: check primitive fields first
        if (
            current.firstName !== original.firstName ||
            current.middleName !== original.middleName ||
            current.lastName !== original.lastName ||
            current.dateOfBirth !== original.dateOfBirth ||
            current.gender !== original.gender ||
            current.lob !== original.lob
        ) {
            return true;
        }

        // Check arrays
        if (current.degrees?.length !== original.degrees?.length ||
            current.languages?.length !== original.languages?.length) {
            return true;
        }

        // Check array contents
        const degreesChanged = current.degrees?.some((d, i) => d !== original.degrees[i]);
        const languagesChanged = current.languages?.some((l, i) => l !== original.languages[i]);

        return degreesChanged || languagesChanged;
    }, []);

    // Function to validate form with optional error display
    const validateForm = useCallback((data) => {
        const { fieldErrors, generalErrors } = GeneralInfoValidation.validateForm(data);
        return {
            isValid: Object.keys(fieldErrors).length === 0 && generalErrors.length === 0,
            fieldErrors
        };
    }, []);

    // Calculate changes between original and current data
    const calculateChanges = useCallback(() => {
        if (!originalData || !hasDataChanged(formState, originalData)) {
            return { hasChanges: false };
        }

        // Helper functions defined locally
        const getFieldDisplayName = (key) => {
            const displayNames = {
                firstName: 'First Name',
                middleName: 'Middle Name',
                lastName: 'Last Name',
                degrees: 'Degrees',
                dateOfBirth: 'Date of Birth',
                gender: 'Gender',
                languages: 'Languages',
                npi: 'NPI',
                lob: 'LOB'
            };
            return displayNames[key] || key;
        };

        const getLobString = (lobCode) => {
            switch (lobCode) {
                case 'AUTO': return 'Auto';
                case 'WC': return 'Workers Comp';
                case 'BOTH': return 'Auto, Workers Comp';
                default: return '';
            }
        };

        // Build detailed changes
        const directChanges = [];
        Object.keys(formState).forEach(key => {
            let areEqual = false;

            if (Array.isArray(formState[key]) && Array.isArray(originalData[key])) {
                // For arrays, compare sorted versions
                areEqual = formState[key].length === originalData[key].length &&
                    formState[key].every((val, idx) => val === originalData[key][idx]);
            } else {
                areEqual = formState[key] === originalData[key];
            }

            if (!areEqual) {
                // Format values for display
                const fieldName = getFieldDisplayName(key);
                let originalValue = Array.isArray(originalData[key])
                    ? originalData[key].join(', ')
                    : (originalData[key] || 'Not Set');

                let newValue = Array.isArray(formState[key])
                    ? formState[key].join(', ')
                    : formState[key];

                directChanges.push({
                    field: fieldName,
                    originalValue,
                    newValue
                });
            }
        });

        const apiUpdateData = {
            phyFirstName: formState.firstName,
            phyMiddleName: formState.middleName,
            phyLastName: formState.lastName,
            phyDateOfBirth: formState.dateOfBirth,
            phyGender: formState.gender,
            degreeStr: formState.degrees.join(', '),
            languageStr: formState.languages.join(', '),
            lobString: getLobString(formState.lob)
        };

        // Create change tracking object
        const changeObject = {
            physicianInfo: {
                id: physician?.id,
                name: `${physician?.phyLastName}, ${physician?.phyFirstName}`,
                tin: selectedTin,
                npi: physician?.prvNpi
            },
            dateRequested: new Date().toISOString(),
            changes: directChanges,
            originalData: originalData,
            currentData: formState
        };

        const { isValid } = validateForm(formState);

        return {
            hasChanges: true,
            apiData: apiUpdateData,
            changeTracking: changeObject,
            isValid
        };
    }, [formState, originalData, physician, selectedTin, validateForm, hasDataChanged]);

    // Highly optimized change detection with throttling
    useEffect(() => {
        if (!originalData) return;

        // Clear previous timer
        if (changeDetectionTimerRef.current) {
            clearTimeout(changeDetectionTimerRef.current);
        }

        // Set a new timer with longer delay (800ms)
        changeDetectionTimerRef.current = setTimeout(() => {
            const changed = hasDataChanged(formState, originalData);

            // Only update if the change status is different
            if (changed !== hasChanges) {
                setHasChanges(changed);

                // If we have changes, mark as not validated
                if (changed) {
                    setIsValidated(false);

                    // Notify parent about unsaved changes
                    if (onSectionHasChanges) {
                        onSectionHasChanges(true);
                    }
                }
            }
        }, 800); // Increased throttle time

        // Cleanup timer on unmount
        return () => {
            if (changeDetectionTimerRef.current) {
                clearTimeout(changeDetectionTimerRef.current);
            }
        };
    }, [formState, originalData, onSectionHasChanges, hasDataChanged, hasChanges]);

    // Expose validation function to parent
    useEffect(() => {
        if (onValidationFunction) {
            onValidationFunction(() => {
                const { isValid, fieldErrors } = validateForm(formState);

                if (!isValid) {
                    dispatchErrors({ type: 'SET_ERRORS', errors: fieldErrors });
                }

                setIsValidated(isValid);
                return isValid;
            });
        }
    }, [onValidationFunction, validateForm, formState]);

    // Expose methods to parent via ref
    useImperativeHandle(ref, () => ({
        getFormData: () => {
            // Validate first if not already validated
            let isValid = isValidated;
            if (!isValid) {
                const validationResult = validateForm(formState);
                isValid = validationResult.isValid;

                if (!isValid) {
                    dispatchErrors({ type: 'SET_ERRORS', errors: validationResult.fieldErrors });
                }

                setIsValidated(isValid);
            }

            return {
                pristineObject: originalData,
                modifiedObject: hasChanges ? formState : null,
                isValid: isValid
            };
        },
        validateForm: () => {
            const { isValid, fieldErrors } = validateForm(formState);

            if (!isValid) {
                dispatchErrors({ type: 'SET_ERRORS', errors: fieldErrors });
            }

            setIsValidated(isValid);
            return isValid;
        },
        resetToOriginal: handleReset
    }), [originalData, formState, isValidated, hasChanges, validateForm]);

    // Highly optimized field change handler with batching
    const handleFieldChange = useCallback((field, value) => {
        // Store the update in the pending updates ref
        pendingUpdatesRef.current[field] = value;

        // Clear any existing error for this field immediately
        dispatchErrors({ type: 'CLEAR_ERROR', field });

        // Clear any existing timer
        if (batchUpdateTimerRef.current) {
            clearTimeout(batchUpdateTimerRef.current);
        }

        // Schedule a batch update
        batchUpdateTimerRef.current = setTimeout(() => {
            // Apply all pending updates at once
            const updates = { ...pendingUpdatesRef.current };

            // Special handling for degrees (sort them)
            if (updates.degrees) {
                updates.degrees = [...updates.degrees].sort((a, b) => a.localeCompare(b));
            }

            // Dispatch the batch update
            dispatchForm({ type: 'BATCH_UPDATE', updates });

            // Clear the pending updates
            pendingUpdatesRef.current = {};
        }, 500); // Batch updates every 500ms
    }, []);

    // Reset form handler
    const handleReset = useCallback((e) => {
        if (e) e.preventDefault();

        if (originalData) {
            // Reset form state to original data
            dispatchForm({ type: 'RESET', data: { ...originalData } });
            dispatchErrors({ type: 'RESET' });
            setIsValidated(false);

            // Notify parent component about the reset action
            if (onSectionDiscardChanges) {
                onSectionDiscardChanges({
                    pristineObject: originalData,
                    modifiedObject: null
                });
            }

            // Notify parent that this section no longer has unsaved changes
            if (onSectionHasChanges) {
                onSectionHasChanges(false);
            }

            // Show a message that changes were discarded
            setLocalSaveMessage("All changes discarded");
            setTimeout(() => setLocalSaveMessage(""), 3000);
        }
    }, [originalData, onSectionDiscardChanges, onSectionHasChanges]);

    // Handler for the "Review Changes" button
    const handleShowChanges = useCallback((e) => {
        if (e) e.preventDefault();

        // Validate the form first
        const { isValid, fieldErrors } = validateForm(formState);

        if (!isValid) {
            dispatchErrors({ type: 'SET_ERRORS', errors: fieldErrors });
            setLocalSaveMessage("Please fix validation errors before reviewing changes");
            setTimeout(() => setLocalSaveMessage(""), 3000);
            return;
        }

        // Get the current changes
        const changes = calculateChanges();

        if (!changes.hasChanges) {
            setLocalSaveMessage("No changes to review");
            setTimeout(() => setLocalSaveMessage(""), 3000);
            return;
        }

        // Set changes summary for display
        setChangesSummary(changes.changeTracking.changes);

        // Show changes dialog
        setShowChangesSummary(true);

        // Mark as validated
        setIsValidated(true);

    }, [formState, validateForm, calculateChanges, originalData, hasChanges]);

    const handleCloseChangesSummary = useCallback(() => {
        setShowChangesSummary(false);
    }, []);

    return (
        <>
            <form
                ref={formRef}
                id="physician-edit-form"
                style={{ width: '100%' }}
                noValidate
                onSubmit={(e) => e.preventDefault()}
            >
                <Grid container spacing={1}>
                    <Grid item xs={4}>
                        <OptimizedField
                            field="firstName"
                            label="First Name"
                            value={formState.firstName}
                            onChange={handleFieldChange}
                            required
                            error={errors.firstName}
                        />
                    </Grid>
                    <Grid item xs={4}>
                        <OptimizedField
                            field="middleName"
                            label="Middle Name"
                            value={formState.middleName}
                            onChange={handleFieldChange}
                        />
                    </Grid>
                    <Grid item xs={4}>
                        <OptimizedField
                            field="lastName"
                            label="Last Name"
                            value={formState.lastName}
                            onChange={handleFieldChange}
                            required
                            error={errors.lastName}
                        />
                    </Grid>

                    <Grid item xs={4}>
                        <OptimizedField
                            field="dateOfBirth"
                            label="Date of Birth"
                            type="date"
                            value={formState.dateOfBirth}
                            onChange={handleFieldChange}
                            required
                            error={errors.dateOfBirth}
                        />
                    </Grid>
                    <Grid item xs={4}>
                        <OptimizedField
                            field="gender"
                            label="Gender"
                            value={formState.gender}
                            onChange={handleFieldChange}
                            required
                            error={errors.gender}
                            select
                        >
                            <MenuItem value="M">Male</MenuItem>
                            <MenuItem value="F">Female</MenuItem>
                        </OptimizedField>
                    </Grid>

                    <Grid item xs={12}>
                        <Divider style={{ margin: '4px 0 8px 0' }} />
                    </Grid>

                    <Grid item xs={4}>
                        <TextField
                            fullWidth
                            label="TIN"
                            value={selectedTin || ''}
                            disabled
                            variant="outlined"
                            InputLabelProps={{ shrink: true }}
                        />
                    </Grid>
                    <Grid item xs={4}>
                        <TextField
                            fullWidth
                            label="NPI"
                            value={formState.npi || ''}
                            disabled
                            variant="outlined"
                            InputLabelProps={{ shrink: true }}
                        />
                    </Grid>

                    <Grid item xs={4}>
                        {/* <OptimizedField
                            field="lob"
                            label="Line of Business"
                            value={formState.lob}
                            onChange={handleFieldChange}
                            required
                            error={errors.lob}
                            select

                        >
                            {LOB_OPTIONS.map((option) => (
                                <MenuItem key={option.value} value={option.value} >
                                    {option.label}
                                </MenuItem>
                            ))}
                        </OptimizedField> */}
                        <TextField
                            fullWidth
                            label="Line of Business"
                            //value={formState.lob ? LOB_OPTIONS.find(option => option.value === formState.lob)?.label || formState.lob : ''}
                            value={physician.lobString}
                            disabled
                            variant="outlined"
                            InputLabelProps={{ shrink: true }}
                        />
                    </Grid>

                    <Grid item xs={4}>
                        {/* Empty space for alignment */}
                    </Grid>

                    <Grid item xs={12}>
                        <Divider style={{ margin: '16px 0' }} />
                    </Grid>

                    <Grid item xs={6}>
                        <OptimizedCheckboxSelection
                            title="Professional Degrees (Required)"
                            value={formState.degrees}
                            onChange={handleFieldChange}
                            options={availableDegrees}
                            field="degrees"
                            error={errors.degrees}
                            height="200px"
                        />
                    </Grid>

                    <Grid item xs={6}>
                        <OptimizedCheckboxSelection
                            title="Spoken Languages (Optional)"
                            value={formState.languages}
                            onChange={handleFieldChange}
                            options={availableLanguages}
                            field="languages"
                            error={errors.languages}
                            height="200px"
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <Divider style={{ margin: '12px 0 8px 0' }} />
                    </Grid>

                    {/* Reset Button at Bottom Center */}
                    <Grid item xs={12}>
                        <Box display="flex" justifyContent="center" gap={2}>
                            <Button
                                type="button"
                                variant="outlined"
                                onClick={handleReset}
                                size="large"
                                startIcon={<UndoIcon />}
                            >
                                Discard Changes
                            </Button>
                            <Button
                                type="button"
                                variant="contained"
                                color="primary"
                                onClick={handleShowChanges}
                                size="large"
                                disabled={!hasChanges}
                                startIcon={<VisibilityIcon />}
                            >
                                Review Changes
                            </Button>
                        </Box>
                    </Grid>

                    {/* Display local save message if present */}
                    {localSaveMessage && (
                        <Grid item xs={12} style={{ marginTop: '16px', textAlign: 'center' }}>
                            <Typography
                                color={localSaveMessage.includes("fix") ? "error" : "primary"}
                                variant="body2"
                            >
                                {localSaveMessage}
                            </Typography>
                        </Grid>
                    )}
                </Grid>
            </form>

            {/* Changes Summary Dialog - extracted as a separate component */}
            <ChangesSummaryDialog
                open={showChangesSummary}
                onClose={handleCloseChangesSummary}
                changesSummary={changesSummary}
            />
        </>
    );
});

export default PhysicianGeneralEdit;